import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/Button';

interface GracePeriodBannerProps {
  graceTier: string;
  graceExpiresAt: string;
  actualPlan: string;
}

const TIER_LABELS: Record<string, string> = {
  core: 'Core',
  pro: 'Pro',
  leader: 'Leader',
};

/**
 * Banner shown to users who are on a grace period tier bump.
 * Communicates what they have access to, when it expires, and how to keep it.
 */
const GracePeriodBanner: React.FC<GracePeriodBannerProps> = ({
  graceTier,
  graceExpiresAt,
  actualPlan,
}) => {
  const navigate = useNavigate();
  const expiresDate = new Date(graceExpiresAt);
  const now = new Date();
  const daysRemaining = Math.max(0, Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  const graceLabel = TIER_LABELS[graceTier] || graceTier;
  const actualLabel = TIER_LABELS[actualPlan] || actualPlan || 'Free';

  if (daysRemaining <= 0) return null;

  const isUrgent = daysRemaining <= 14;

  return (
    <div className={`border rounded-lg px-4 py-3 mb-4 ${
      isUrgent
        ? 'bg-warning/10 border-warning'
        : 'bg-accent/10 border-accent'
    }`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-medium text-text-primary m-0">
            You have {graceLabel} access
            {actualPlan === 'free' ? ' as a thank-you for being an early user' : ' during your transition'}
          </p>
          <p className="text-xs text-text-secondary mt-1 m-0">
            {isUrgent
              ? `Expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. `
              : `Active until ${expiresDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. `
            }
            {actualPlan === 'free'
              ? `Upgrade to ${graceLabel} to keep these features.`
              : `Your ${actualLabel} plan will take effect after this period.`
            }
          </p>
        </div>
        {actualPlan === 'free' && (
          <Button
            size="sm"
            onClick={() => navigate(`/billing/checkout?plan=${graceTier}`)}
          >
            Keep {graceLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default GracePeriodBanner;
